import React from 'react';
import {
  Alert,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DollarOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import TransportFormSection from './TransportFormSection';
import { formatMoneyPl } from '../utils/currency';
import {
  DEFAULT_TRANSPORT_CURRENCY,
  type SharedTransportPreviewRow,
} from './sharedTransportFormUtils';
import {
  buildCostAllocationMethodOptionDefs,
  DEFAULT_ALLOCATION_METHOD,
  formatAllocationKeyReasons,
  MAX_MONEY_AMOUNT,
  POLISH_DECIMAL_INPUT_NUMBER_PROPS,
  type AllocationKeyAvailability,
  type AllocationMethod,
  type TransportCostAllocationWarning,
} from '../utils/transportCostAllocation';

const { Text } = Typography;

// Item warnings that repeat what the "key does not cover every position"
// alert already says (same products, same reason).
const KEY_COVERAGE_WARNING_CODES = new Set([
  'MIXED_UNITS', 'MISSING_DIMENSIONS', 'MISSING_VALUE', 'COUNTED_IN_PIECES_ONLY', 'ZERO_TOTAL_METRIC',
]);

interface TransportCostsSectionProps {
  /** Section number (owner 2026-10-10: „Gdzie jedzie” shifts the following sections). */
  step?: number;
  canEditTransportCosts: boolean;
  showRateFields: boolean;
  /**
   * Faktura przewoźnika of the truck: its amount is the rate and is changed
   * only on the truck card (backend keeps it on an ordinary save).
   */
  invoiceRate?: { number: string; amount: number; currency: string } | null;
  multiOrder: boolean;
  allocationPreview: SharedTransportPreviewRow[];
  previewColumns: ColumnsType<SharedTransportPreviewRow>;
  currency?: string;
  // Count of loaded cargo lines (quantity > 0).
  cargoLineCount: number;
  /** All positions the key splits over (customer lines or carried ZD lines). */
  positionCount?: number;
  /** Currency of the truck's ZD (factory trucks), for the B6 warning. */
  supplierOrderCurrency?: string | null;
  warnings: TransportCostAllocationWarning[];
  renderAllocationWarning: (warning: TransportCostAllocationWarning, index: number) => React.ReactNode;
  // Orders whose m²/m³ split basis misses product dimensions on ≥1 loaded
  // line (backend MISSING_DIMENSIONS). Their share is understated, never guessed.
  missingDimensionOrders?: string[];
  /** The truck key chosen in the form. */
  costAllocationMethod: AllocationMethod;
  /**
   * Which keys can weigh the positions on the truck (edit: server
   * `allocationKeyAvailability`; create: the frontend mirror). Informational:
   * an unavailable option is greyed out with its reason, nothing is blocked.
   */
  keyAvailability: AllocationKeyAvailability;
  /** Goods value key: shares come from the server (saved = true in edit mode). */
  valueKey?: { saved: boolean } | null;
  /**
   * Live „Podział kosztu auta” (decision 2026-10-04): replaces the per-order
   * table below when given.
   */
  splitPreview?: React.ReactNode;
  /**
   * Usability round 2026-10-05: the default key's real measure on this truck
   * („Domyślny — według objętości m³”) instead of the bare „szt.” label.
   */
  defaultKeyLabel?: string | null;
}

/**
 * „Koszty transportu”: one amount + currency and ONE key for the truck
 * (decision 2026-09-30) — szt. (default), m², m³, wartość towaru, ręcznie.
 */
const TransportCostsSection: React.FC<TransportCostsSectionProps> = ({
  step = 4,
  canEditTransportCosts,
  showRateFields,
  invoiceRate = null,
  multiOrder,
  allocationPreview,
  previewColumns,
  currency,
  cargoLineCount,
  positionCount,
  supplierOrderCurrency,
  warnings,
  renderAllocationWarning,
  missingDimensionOrders = [],
  costAllocationMethod,
  keyAvailability,
  valueKey = null,
  splitPreview = null,
  defaultKeyLabel = null,
}) => {
  const { t } = useTranslation(['transport']);
  const transportRateValue = Form.useWatch('transportRate');

  const keyOptions = buildCostAllocationMethodOptionDefs().map((definition) => {
    const isDefaultKey = definition.value === DEFAULT_ALLOCATION_METHOD;
    const label = isDefaultKey && defaultKeyLabel ? defaultKeyLabel : t(`transport:${definition.labelKey}`);
    const state = keyAvailability[definition.value];
    // The default key always weighs every load (m³ → m² → szt. → po równo).
    const unavailable = state && !(isDefaultKey && defaultKeyLabel) ? !state.available : false;
    const reason = unavailable
      ? t('transport:allocationKey.unavailableReason', {
        key: label,
        reasons: formatAllocationKeyReasons(state.reasons),
      })
      : null;
    const explanation = definition.tooltipKey ? t(`transport:${definition.tooltipKey}`) : null;
    return {
      value: definition.value,
      // A saved key the positions no longer support stays selected (the
      // server splits by it with warnings) — only other options are greyed.
      disabled: unavailable && definition.value !== costAllocationMethod,
      title: label,
      label: (
        <Tooltip title={reason || explanation} placement="right">
          <div className="transport-key-option" data-testid={`transport-key-option-${definition.value}`}>
            <Space size={4}>
              <span>{label}</span>
              {explanation && !reason ? <InfoCircleOutlined style={{ color: 'var(--app-text-muted)' }} /> : null}
            </Space>
            {reason ? (
              <Text type="secondary" style={{ display: 'block', fontSize: 12, whiteSpace: 'normal' }}>
                {reason}
              </Text>
            ) : null}
            {isDefaultKey && defaultKeyLabel ? (
              // Round 2 (U2): next to the plain „m³” option, say what differs.
              <Text type="secondary" style={{ display: 'block', fontSize: 12, whiteSpace: 'normal' }} data-testid="transport-key-default-explain">
                {t('transport:allocationKey.defaultExplain')}
              </Text>
            ) : null}
          </div>
        </Tooltip>
      ),
    };
  });

  const currentState = keyAvailability[costAllocationMethod];
  // With the live split preview, one warning lists the positions that really
  // get 0 (TransportCostSplitPreview); the coverage alerts here would repeat
  // it (usability round 2026-10-05).
  const currentUnavailable = !splitPreview && Boolean(currentState && !currentState.available);
  const currentLabel = t(`transport:${buildCostAllocationMethodOptionDefs()
    .find((definition) => definition.value === costAllocationMethod)?.labelKey || 'commercialAllocationByCommercialQty'}`);
  const visibleWarnings = currentUnavailable || splitPreview
    ? warnings.filter((warning) => !KEY_COVERAGE_WARNING_CODES.has(warning.code))
    : warnings;

  return (
    <TransportFormSection
      sectionKey="costs"
      step={step}
      icon={<DollarOutlined />}
      label={canEditTransportCosts ? t('transport:sections.costs') : t('transport:allocationKey.label')}
      summary={canEditTransportCosts && typeof transportRateValue === 'number' && Number.isFinite(transportRateValue)
        ? formatMoneyPl(transportRateValue, currency || 'PLN')
        : undefined}
    >
      {/* With the live split the rows say the same (owner 2026-10-10). */}
      {multiOrder && costAllocationMethod !== 'MANUAL' && !splitPreview ? (
        <Alert
          type="info"
          showIcon
          message={t('transport:allocation.multiOrderHint')}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {/* Product decision (2026-07-08): the transport price is ONE amount +
          currency; the system derives the per-unit costs from it. */}
      {/* Owner 2026-10-10: amount, currency and the key in one row. */}
      <div className="transport-costs-grid">
        {canEditTransportCosts && showRateFields && invoiceRate ? (
          <div className="transport-costs-grid__invoice" data-testid="transport-rate-from-invoice-form">
            {/* Keep the rate and currency registered so the cost preview and the
                summary bar read the invoice amount (the backend ignores both). */}
            <Form.Item name="transportRate" hidden><InputNumber /></Form.Item>
            <Form.Item name="currency" hidden><Input /></Form.Item>
            <Form.Item label={t('transport:fields.transportRate')} extra={t('transport:invoice.formHint')}>
              <div className="transport-costs-grid__invoice-value">
                {t('transport:invoice.formValue', {
                  amount: formatMoneyPl(invoiceRate.amount, invoiceRate.currency),
                  number: invoiceRate.number,
                })}
              </div>
            </Form.Item>
          </div>
        ) : null}
        {canEditTransportCosts && showRateFields && !invoiceRate && (
          <>
            <div className="transport-costs-grid__rate">
              <Form.Item
                name="transportRate"
                label={t('transport:fields.transportRate')}
                rules={[
                  {
                    type: 'number',
                    min: 0,
                    max: MAX_MONEY_AMOUNT,
                    message: t('transport:transportRateOutOfRange', { max: MAX_MONEY_AMOUNT }),
                  },
                ]}
              >
                {/* No min/max clamp: antd silently coerces an out-of-range entry
                    on blur and would save the coerced value. The rule above keeps
                    the entry visible with an inline error and blocks save. */}
                <InputNumber
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder={t('transport:placeholders.transportRateLater')}
                  {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                />
              </Form.Item>
              {transportRateValue === null || transportRateValue === undefined ? (
                <Text type="secondary" style={{ display: 'block', marginTop: -16, fontSize: 12 }}>
                  {t('transport:fields.transportRateLaterHint')}
                </Text>
              ) : null}
            </div>
            <div className="transport-costs-grid__currency">
              <Form.Item
                name="currency"
                label={t('transport:fields.currency')}
                // Round 3 (B6): never switched under a typed cost — warn instead.
                extra={supplierOrderCurrency && currency && supplierOrderCurrency !== currency ? (
                  <Text type="warning" data-testid="transport-currency-zd-warning">
                    {t('transport:currencyZdWarning', { currency: supplierOrderCurrency })}
                  </Text>
                ) : undefined}
              >
                <Select options={[{ value: 'USD', label: 'USD' }, { value: 'EUR', label: 'EUR' }, { value: 'PLN', label: 'PLN' }]} />
              </Form.Item>
            </div>
          </>
        )}
        {canEditTransportCosts ? (
            <div className="transport-costs-grid__key">
              <Form.Item
                name="costAllocationMethod"
                label={t('transport:allocationKey.label')}
                tooltip={t('transport:allocationKey.tooltip')}
                extra={!multiOrder && (positionCount ?? cargoLineCount) <= 1 ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('transport:costAllocationSingleLineHint')}
                  </Text>
                ) : undefined}
                style={{ marginBottom: 0 }}
              >
                <Select
                  options={keyOptions}
                  popupMatchSelectWidth={false}
                  dropdownStyle={{ maxWidth: 520, minWidth: 320 }}
                  optionLabelProp="title"
                  data-testid="transport-cost-allocation-key"
                  aria-label={t('transport:allocationKey.label')}
                />
              </Form.Item>
            </div>
        ) : null}
      </div>

      {canEditTransportCosts ? (
        <>
          {currentUnavailable ? (
            <Alert
              type="warning"
              showIcon
              data-testid="transport-key-unavailable-warning"
              message={t('transport:allocationKey.currentUnavailable', { key: currentLabel })}
              description={formatAllocationKeyReasons(currentState.reasons)}
              style={{ marginTop: 16 }}
            />
          ) : null}
          {valueKey ? (
            <Alert
              type="info"
              showIcon
              data-testid="transport-value-key-info"
              message={valueKey.saved
                ? t('transport:allocationKey.valueSaved')
                : t('transport:allocationKey.valueOnSave')}
              style={{ marginTop: 16 }}
            />
          ) : null}
        </>
      ) : (
        // Hidden but still registered when the user can't edit costs: the
        // key round-trips unchanged.
        <Form.Item name="costAllocationMethod" hidden noStyle>
          <Input />
        </Form.Item>
      )}

      {canEditTransportCosts && missingDimensionOrders.length > 0 && !currentUnavailable && !splitPreview ? (
        <Alert
          type="warning"
          showIcon
          data-testid="transport-missing-dimensions-warning"
          message={t('transport:allocation.missingDimensionsWarning', {
            orders: missingDimensionOrders.join(', '),
          })}
          style={{ marginTop: 16 }}
        />
      ) : null}

      {canEditTransportCosts && splitPreview}

      {/* Per-order allocation preview — only meaningful with ≥2 orders. */}
      {canEditTransportCosts && multiOrder && !splitPreview && (
        <>
          <Divider orientation="left" style={{ marginTop: 8 }}>
            {t('transport:allocation.preview')}
          </Divider>
          <Table<SharedTransportPreviewRow>
            rowKey="orderId"
            size="small"
            pagination={false}
            locale={{ emptyText: t('transport:pleaseSelectAtLeastOneOrder') }}
            dataSource={allocationPreview}
            columns={previewColumns}
          />
          <Text type="secondary">
            {valueKey
              ? t('transport:allocationKey.previewValueHint')
              : t('transport:allocation.previewCurrencyHintSingle', { currency: currency || DEFAULT_TRANSPORT_CURRENCY })}
          </Text>
        </>
      )}

      {/* Item-level allocation warnings stay with the cost section. */}
      {canEditTransportCosts && cargoLineCount > 0
        ? visibleWarnings.map(renderAllocationWarning)
        : null}
    </TransportFormSection>
  );
};

export default TransportCostsSection;
